import { Component } from 'react';
import { Cell } from '@table-library/react-table-library/table';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { countryColumns } from '../keys';
import { countryLabel, sourceLabel } from '../sources';

/**
 * Block 04 — where the selected source's traffic was served, by country.
 *
 * A country row counts the clicks and impressions Google served in that country. It is not a count
 * of people, it says nothing about who they were or which city they were in, and it is not a
 * share of any market: the report has this property's own traffic and nothing to divide it by.
 * A country missing from the list is a country Search Console returned nothing for, not a zero.
 *
 * Picking a country scopes the whole report to it — every block, not just this one — through the
 * same country filter the scope bar offers, so the bar shows it and the bar can take it off. It is
 * applied rather than drafted: the reader has already chosen it by clicking a row, and making them
 * press Apply as well would be asking twice.
 */
class CountryDistribution extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
        this.actionColumn = this.actionColumn.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
    }

    /** Searching reads both the country's name and the code it was returned under. */
    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter(
            (item) =>
                item.key?.toLowerCase().includes(term) ||
                countryLabel(item.key).toLowerCase().includes(term),
        );
    }

    /** Clicking the row does what its button does: filter to the country, or take it back off. */
    handleRowClick(item) {
        this.props.onSelectCountry(item.key);
    }

    actionColumn() {
        const { t, scope, onSelectCountry } = this.props;

        return {
            label: t('tools.discover_news.countries.filter_column'),
            key: 'filter',
            data: 'key',
            sortType: (array) => array,
            cellSize: 'minmax(130px, 150px)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (csvDownload) return '';

                const active = item.key === scope.country;

                return (
                    <div className="d-flex">
                        <button
                            type="button"
                            className={`table-action-main-btn ${active ? 'is-picked' : ''}`}
                            aria-pressed={active}
                            onClick={() => onSelectCountry(item.key)}
                        >
                            {active && <i className="icon-check-icon" aria-hidden="true" />}
                            {active
                                ? t('tools.discover_news.countries.filtered')
                                : t('tools.discover_news.countries.filter')}
                        </button>
                    </div>
                );
            },
        };
    }

    render() {
        const { t, scope, status, error, rows, onClearCountry } = this.props;

        const columns = countryColumns(t, { hasPosition: reportsPosition(scope.searchType) });
        const tableColumns = [...columns, this.actionColumn()];
        const source = sourceLabel(t, scope.searchType);

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">{t('tools.discover_news.countries.title')}</p>
                        <p className="gsc-tool-sub">
                            {t('tools.discover_news.countries.description', { source })} ·{' '}
                            {formatPeriod(scope.current)}
                        </p>
                        <p className="gsc-tool-rows-hint">
                            <i className="icon-info" aria-hidden="true" />
                            {t('tools.discover_news.countries.select_hint')}
                        </p>
                    </div>
                </div>

                <div className="card-body">
                    {/* Stated here as well as in the scope bar: a reader who clicked a row in
                        this block is looking at this block, not at the bar above the page. */}
                    {!!scope.country && (
                        <div className="gsc-tool-chips mt-0 mb-3">
                            <span className="gsc-tool-chip">
                                <span>
                                    {t('tools.discover_news.countries.active', {
                                        country: countryLabel(scope.country),
                                        code: scope.country,
                                    })}
                                </span>
                                <button
                                    type="button"
                                    aria-label={t('tools.discover_news.countries.clear')}
                                    title={t('tools.discover_news.countries.clear')}
                                    onClick={onClearCountry}
                                >
                                    <i className="icon-x-close" />
                                </button>
                            </span>
                        </div>
                    )}

                    {(status === 'idle' || status === 'loading') && <Loading padding={20} />}

                    {/* Its own failure, in its own block: a country request that fails says
                        nothing about the source summary, which stays standing above. */}
                    {status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {status === 'done' && (
                        <>
                            <DataTable
                                nodes={rows}
                                COLUMNS={tableColumns}
                                customColumnsForExport={columns}
                                sortFns={getSortKeys(tableColumns)}
                                additionalStyles={setAdditionalStyles(tableColumns)}
                                searchFunction={this.searchFunction}
                                fileName={`${t('tools.discover_news.countries.title')} — ${source}`}
                                customEmptyMessage={t('tools.discover_news.countries.empty', {
                                    source,
                                })}
                                customContainerStyles="gsc-tool-key-table"
                                rowClassName={(item) =>
                                    item.key === scope.country ? 'dt-row-picked' : ''
                                }
                                onRowClick={this.handleRowClick}
                            />

                            <p className="gsc-tool-table-note">
                                {t('tools.discover_news.countries.not_people')}
                            </p>
                            <p className="gsc-tool-table-note">
                                {t('tools.discover_news.countries.no_reach')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(CountryDistribution);
